import { useRef, useEffect, useCallback, useState } from 'react';
import { preloadedUrls } from '@/services/videoPreloadService';

/** Seconds of preview before looping back to 0. */
const PREVIEW_SECONDS = 4;

/**
 * Manages hover-based video preview for thumbnail tiles.
 *
 * Best practices applied:
 * - Lazy src: set only on first hover — nothing is fetched at render time
 * - `#t=0,N` media fragment: hints the browser/CDN to buffer only the first N seconds
 * - 5-second loop via `timeupdate` (not the `loop` attribute, which loops the whole file)
 * - 100 ms enter-debounce: skips quick mouse-passes, saving network round-trips
 * - Shares `preloadedUrls` with videoPreloadService: if the URL was already
 *   buffered in the background, the video.load() call is skipped entirely so
 *   the first hover plays instantly from the HTTP cache.
 * - Unmount cleanup: removes `src` + calls `load()` to abort in-flight requests
 *   and release the video buffer memory
 *
 * When `src` is undefined the hook still manages play/pause for a static fallback
 * src that was set directly on the <video> element (no lazy loading).
 */
export function useHoverVideoPreview(src?: string) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Holds a cleanup fn that removes a pending loadeddata listener
  const loadedCleanupRef = useRef<(() => void) | null>(null);
  // Holds a cleanup fn that removes a pending error listener
  const errorCleanupRef = useRef<(() => void) | null>(null);
  // True when the video URL fails to load — keeps the thumbnail visible as fallback
  const [videoError, setVideoError] = useState(false);
  // True only once the video is actually playing — prevents black flash during load
  const [isVideoVisible, setIsVideoVisible] = useState(false);

  // Loop within the first PREVIEW_SECONDS when a real src is active
  const handleTimeUpdate = useCallback(() => {
    const video = videoRef.current;
    if (video && video.currentTime >= PREVIEW_SECONDS) {
      video.currentTime = 0;
    }
  }, []);

  const handleMouseEnter = useCallback(() => {
    // 100 ms debounce — avoids a network hit on a fast mouse-pass
    hoverTimerRef.current = setTimeout(() => {
      const video = videoRef.current;
      if (!video) return;

      let srcChanged = false;
      if (src) {
        if (!preloadedUrls.has(src)) {
          video.src = `${src}#t=0,${PREVIEW_SECONDS}`;
          preloadedUrls.add(src);
          srcChanged = true;
        } else if (!video.src || !video.src.includes(src)) {
          video.src = `${src}#t=0,${PREVIEW_SECONDS}`;
          srcChanged = true;
        }
      }

      const doPlay = () => {
        loadedCleanupRef.current = null;
        video.currentTime = 0;
        video.play()
          .then(() => setIsVideoVisible(true))   // show only once actually playing
          .catch(() => { /* autoplay blocked — stay hidden */ });
      };

      // Error handler — bad URL / network failure: keep thumbnail visible
      const onError = () => {
        errorCleanupRef.current = null;
        loadedCleanupRef.current = null;
        setVideoError(true);
        setIsVideoVisible(false);
      };
      video.addEventListener('error', onError, { once: true });
      errorCleanupRef.current = () => video.removeEventListener('error', onError);

      // If src was just changed or the video has no buffered data yet,
      // wait for the first frame before seeking — otherwise currentTime=0
      // is silently ignored while the element is in HAVE_NOTHING state.
      if (srcChanged || video.readyState < 2 /* HAVE_CURRENT_DATA */) {
        const onLoaded = () => {
          // Success — clear the error listener, it's no longer needed
          if (errorCleanupRef.current) { errorCleanupRef.current(); errorCleanupRef.current = null; }
          doPlay();
        };
        video.addEventListener('loadeddata', onLoaded, { once: true });
        loadedCleanupRef.current = () => video.removeEventListener('loadeddata', onLoaded);
        // Kick off loading — without this, preload="none" videos never fire loadeddata
        video.load();
        return;
      }

      doPlay();
    }, 100);
  }, [src]);

  const handleMouseLeave = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    // Cancel any pending loadeddata callback so a slow-loading video
    // doesn't start playing after the user has already moved away
    if (loadedCleanupRef.current) {
      loadedCleanupRef.current();
      loadedCleanupRef.current = null;
    }
    if (errorCleanupRef.current) {
      errorCleanupRef.current();
      errorCleanupRef.current = null;
    }
    setIsVideoVisible(false);
    const video = videoRef.current;
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
  }, []);

  // Attach timeupdate only when a real preview src is provided
  useEffect(() => {
    if (!src) return;
    const video = videoRef.current;
    if (!video) return;
    video.addEventListener('timeupdate', handleTimeUpdate);
    return () => video.removeEventListener('timeupdate', handleTimeUpdate);
  }, [src, handleTimeUpdate]);

  // Reset error + visibility flags whenever the src changes
  useEffect(() => {
    setVideoError(false);
    setIsVideoVisible(false);
  }, [src]);

  // Release video resources on unmount — prevents memory leaks
  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
      if (loadedCleanupRef.current) loadedCleanupRef.current();
      if (errorCleanupRef.current) errorCleanupRef.current();
      const video = videoRef.current;
      if (video && src) {
        video.pause();
        video.removeAttribute('src');
        video.load(); // aborts any in-flight network requests + frees buffer
      }
    };
    // Intentionally omitting `src` — we want the refs captured at mount time for cleanup
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { videoRef, handleMouseEnter, handleMouseLeave, videoError, isVideoVisible };
}
